import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, MessageSquare, X, ArrowLeftRight, RotateCcw, Eye, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

/*
 * What the client still owes us, per service in scope — the PM's checklist.
 * Each service brings its list from Admin → Services (`required_materials`)
 * when it is put into scope; the PM ticks items as they arrive, adds what the
 * project needs on top, removes what it doesn't. The client sees the same list
 * read-only in their cabinet. WS task 23241245 (ops lead's spec + mockup).
 *
 * Row shape: {id, service, name, received, received_at, comment, source, removed}.
 */

// Same folding as the backend's _material_key: one material asked for by two
// services is one file, handed over once.
export const materialKey = (name) => String(name || "").toLowerCase().replace(/[^a-z0-9а-яіїєґ]+/g, "");

const newId = () => `m-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const today = () => new Date().toISOString().slice(0, 10);

// Service names are matched folded too: type names carry stray spaces
// ("360° Video Sphere ") while the scope implied by payments is trimmed, and an
// exact lookup found no list for Video Sphere.
const svcKey = materialKey;
const sameService = (a, b) => svcKey(a) === svcKey(b);
const templateFor = (templates, svc) => templates[svcKey(svc)] || [];

/** {folded service name: [material, ...]} from Admin → Services. */
export function useMaterialTemplates() {
  const [templates, setTemplates] = useState({});
  useEffect(() => {
    let alive = true;
    api.get("/settings/service-types")
      .then((res) => {
        if (!alive) return;
        const map = {};
        (res.data || []).forEach((st) => { const k = svcKey(st.name); if (k && !(k in map)) map[k] = st.required_materials || []; });
        setTemplates(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return templates;
}

/**
 * Rows for `services` from their Admin lists, skipping what the project already
 * has for that service (removed rows included). A material already received
 * under another service comes in received. Mirrors _attach_template_materials.
 */
export function attachTemplateMaterials(materials, services, templates) {
  const out = [...(materials || [])];
  const receivedAt = {};
  out.forEach((m) => {
    if (m.received && !m.removed) {
      const k = materialKey(m.name);
      if (!(k in receivedAt)) receivedAt[k] = m.received_at || null;
    }
  });
  services.forEach((svc) => {
    const have = new Set(out.filter((m) => sameService(m.service, svc)).map((m) => materialKey(m.name)));
    templateFor(templates, svc).forEach((name) => {
      const k = materialKey(name);
      if (!k || have.has(k)) return;
      have.add(k);
      const got = k in receivedAt;
      out.push({ id: newId(), service: svc, name, received: got, received_at: got ? receivedAt[k] : null, source: "template" });
    });
  });
  return out;
}

// `review` — folded names of services whose list filled in by itself on a
// project already in work. The client doesn't see those lists until the PM has
// ticked what arrived and pressed "Show to client" (`onShowToClient`).
export default function ServiceMaterialsPanel({ services, materials, templates, onChange, review = [], onShowToClient }) {
  const [filter, setFilter] = useState("all"); // all | missing | received
  const [open, setOpen] = useState({});
  const [showRemoved, setShowRemoved] = useState({});
  const [editingComment, setEditingComment] = useState(null);

  const live = useMemo(() => (materials || []).filter((m) => !m.removed && services.some((s) => sameService(s, m.service))), [materials, services]);
  const total = live.length;
  const got = live.filter((m) => m.received).length;

  // Which other services ask for the same material — the mockup's ⇄ tag.
  const servicesByKey = useMemo(() => {
    const map = {};
    live.forEach((m) => {
      const k = materialKey(m.name);
      (map[k] = map[k] || new Set()).add(m.service);
    });
    return map;
  }, [live]);

  const setRow = (id, patch) => onChange((materials || []).map((m) => (m.id === id ? { ...m, ...patch } : m)));

  // Ticking a material ticks it everywhere it is asked for.
  const toggle = (row) => {
    const k = materialKey(row.name);
    const next = !row.received;
    onChange((materials || []).map((m) => (
      !m.removed && materialKey(m.name) === k
        ? { ...m, received: next, received_at: next ? (m.received_at || today()) : null }
        : m
    )));
  };

  const remove = (row) => {
    // Admin-list rows are kept as removed, so re-adding the service or pulling
    // the list again doesn't bring them back; a hand-typed row just goes.
    if (row.source === "template") setRow(row.id, { removed: true });
    else onChange((materials || []).filter((m) => m.id !== row.id));
  };

  const add = (svc, name) => {
    const k = materialKey(name);
    if (!k) return;
    const existing = (materials || []).find((m) => sameService(m.service, svc) && materialKey(m.name) === k);
    if (existing) {
      if (existing.removed) setRow(existing.id, { removed: false });
      return;
    }
    const twin = live.find((m) => materialKey(m.name) === k && m.received);
    onChange([...(materials || []), {
      id: newId(), service: svc, name: name.trim(), received: Boolean(twin),
      received_at: twin ? twin.received_at || null : null, source: "manual",
    }]);
  };

  if (!services.length) return null;
  const allOpen = services.every((s) => open[s]);

  return (
    <div className="pt-1 space-y-1.5" data-testid="service-materials-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-zinc-500 text-[10px] font-medium">Materials from client</span>
        <span className="flex items-center gap-1 text-[10px] text-zinc-500" title="The client sees this list in their cabinet (read-only)">
          <Eye className="h-3 w-3" />{services.some((sv) => review.includes(svcKey(sv))) ? "visible to client, except lists marked below" : "visible to client"}
        </span>
        {total > 0 && (
          <span className="flex items-center gap-1.5 text-[11px] text-zinc-300">
            <b className="font-semibold tabular-nums">{got}/{total}</b>
            <span className="h-1 w-16 rounded-full bg-zinc-800 overflow-hidden">
              <span className="block h-full bg-emerald-500" style={{ width: `${Math.round((got / total) * 100)}%` }} />
            </span>
            <span className="text-zinc-500">received</span>
          </span>
        )}
        {total > 0 && (
          <span className="flex rounded-md border border-zinc-700 overflow-hidden text-[10px]">
            {[["all", "All", total], ["missing", "Missing", total - got], ["received", "Received", got]].map(([k, label, n]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                className={cn("px-1.5 py-0.5", filter === k ? "bg-zinc-700 text-zinc-50" : "text-zinc-400 hover:text-zinc-100")}>
                {label} <span className="text-zinc-500">{n}</span>
              </button>
            ))}
          </span>
        )}
        <button type="button" className="text-[10px] text-sky-400 hover:text-sky-300 ml-auto"
          onClick={() => setOpen(Object.fromEntries(services.map((s) => [s, !allOpen])))}>
          {allOpen ? "Collapse all" : "Expand all"}
        </button>
      </div>

      <div className="space-y-1">
        {services.map((svc) => {
          const rows = (materials || []).filter((m) => sameService(m.service, svc));
          const active = rows.filter((m) => !m.removed);
          const removed = rows.filter((m) => m.removed);
          const n = active.length, g = active.filter((m) => m.received).length;
          const shown = active.filter((m) => filter === "all" || (filter === "missing" ? !m.received : m.received));
          const have = new Set(rows.map((m) => materialKey(m.name)));
          const notPulled = templateFor(templates, svc).filter((name) => !have.has(materialKey(name)));
          // Never pulled: the client sees "the list is being prepared" for it.
          const neverPulled = rows.length === 0;
          const isOpen = Boolean(open[svc]);
          const inReview = n > 0 && review.includes(svcKey(svc));
          return (
            <div key={svc} className="rounded-md border border-zinc-800 bg-zinc-900/40" data-testid={`materials-service-${svc}`}>
              <button type="button" onClick={() => setOpen({ ...open, [svc]: !isOpen })}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left">
                <span className="text-[12px] text-zinc-100 font-medium truncate">{svc}</span>
                <span className="ml-auto flex items-center gap-2 shrink-0">
                  {n > 0 ? (
                    <>
                      <span className="h-1 w-14 rounded-full bg-zinc-800 overflow-hidden">
                        <span className="block h-full bg-emerald-500" style={{ width: `${Math.round((g / n) * 100)}%` }} />
                      </span>
                      <span className="text-[11px] tabular-nums text-zinc-300">{g}<span className="text-zinc-500"> / {n}</span></span>
                      <span className={cn("text-[10px] w-[68px] text-right", g === n ? "text-emerald-400" : "text-sky-400")}>
                        {g === n ? "✓ All received" : `${n - g} missing`}
                      </span>
                    </>
                  ) : (
                    <span className="text-[10px] text-amber-400/90" title="The client sees “the list is being prepared” for this service">
                      {neverPulled ? (notPulled.length ? "list not pulled yet" : "no list in Admin") : "nothing needed"}
                    </span>
                  )}
                  <ChevronDown className={cn("h-3.5 w-3.5 text-zinc-500 transition-transform", isOpen && "rotate-180")} />
                </span>
              </button>
              {inReview && (
                <div className="flex flex-wrap items-center gap-2 mx-2 mb-1.5 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] text-amber-200/90"
                  data-testid={`material-review-${svc}`}>
                  <span className="flex-1 min-w-[180px]">
                    Hidden from the client: this list came from Admin while the project was already running.
                    Tick what has arrived, then show it.
                  </span>
                  <button type="button" className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-amber-200 hover:bg-amber-500/20"
                    onClick={() => onShowToClient && onShowToClient(svc)} data-testid={`material-show-${svc}`}>
                    <Eye className="h-3 w-3" />Show to client
                  </button>
                </div>
              )}
              {neverPulled && (
                <div className="flex flex-wrap items-center gap-2 px-2 pb-1.5 -mt-0.5 text-[10px]">
                  {notPulled.length > 0 ? (
                    <button type="button" className="rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-sky-300 hover:bg-sky-500/20"
                      onClick={() => { onChange(attachTemplateMaterials(materials, [svc], templates)); setOpen({ ...open, [svc]: true }); }}
                      title={notPulled.join("\n")} data-testid={`material-pull-${svc}`}>
                      + Pull {notPulled.length} from Admin list
                    </button>
                  ) : (
                    <span className="text-zinc-500">This service has no materials list yet.</span>
                  )}
                  <Link to="/settings/services?tab=services" className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-200"
                    title="Admin → Services → edit the service → “Required materials from client”">
                    <Settings2 className="h-3 w-3" />{notPulled.length ? "edit list" : "set it in Admin → Services"}
                  </Link>
                </div>
              )}

              {isOpen && (
                <div className="border-t border-zinc-800 px-2 py-1 space-y-0.5">
                  {shown.map((m) => {
                    const others = [...(servicesByKey[materialKey(m.name)] || [])].filter((s) => s !== svc);
                    return (
                      <div key={m.id} className={cn("group rounded px-1 py-1", !m.received && "border-l-2 border-sky-500/60")}>
                        <div className="flex items-center gap-2">
                          <Checkbox checked={Boolean(m.received)} onCheckedChange={() => toggle(m)}
                            className="h-3.5 w-3.5 border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                            data-testid="material-checkbox" />
                          <span className={cn("text-[12px] flex-1 min-w-0", m.received ? "text-zinc-400" : "text-zinc-100")}>
                            {m.name}
                            {m.source !== "template" && <span className="ml-1.5 text-[10px] text-zinc-500">added manually</span>}
                            {others.length > 0 && (
                              <span className="ml-1.5 inline-flex items-center gap-0.5 rounded border border-sky-500/30 bg-sky-500/10 px-1 text-[10px] text-sky-300"
                                title="Also needed for these services — ticking it here ticks it there too">
                                <ArrowLeftRight className="h-2.5 w-2.5" />{others.join(", ")}
                              </span>
                            )}
                          </span>
                          <span className={cn("text-[10px] rounded px-1.5 py-px border shrink-0",
                            m.received ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300")}
                            title={m.received && m.received_at ? `Received ${m.received_at}` : undefined}>
                            {m.received ? "Received" : "Not received"}
                          </span>
                          <button type="button" title="Comment (the client sees it)" onClick={() => setEditingComment(editingComment === m.id ? null : m.id)}
                            className={cn("shrink-0", m.comment ? "text-sky-400" : "text-zinc-600 hover:text-zinc-300")}>
                            <MessageSquare className="h-3 w-3" />
                          </button>
                          <button type="button" title="Not needed for this project" onClick={() => remove(m)}
                            className="shrink-0 text-zinc-600 hover:text-rose-400">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        {editingComment === m.id ? (
                          <Input autoFocus defaultValue={m.comment || ""} placeholder="Comment for this material…"
                            className="mt-1 ml-5 h-6 w-[calc(100%-1.25rem)] bg-zinc-800 border-zinc-700 text-[11px] px-2"
                            onBlur={(e) => { setRow(m.id, { comment: e.target.value.trim() }); setEditingComment(null); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } if (e.key === "Escape") setEditingComment(null); }} />
                        ) : m.comment ? (
                          <div className="mt-0.5 ml-5 text-[11px] text-zinc-500">{m.comment}</div>
                        ) : null}
                      </div>
                    );
                  })}
                  {n > 0 && shown.length === 0 && (
                    <div className="px-1 py-1 text-[11px] text-zinc-500">Nothing {filter === "missing" ? "missing" : "received yet"}.</div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Input placeholder={`Add material to ${svc} and press Enter`}
                      className="h-6 flex-1 min-w-[180px] bg-zinc-800 border-zinc-700 border-dashed text-zinc-300 text-[11px] px-2"
                      data-testid={`material-add-${svc}`}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" || !e.target.value.trim()) return;
                        e.preventDefault();
                        add(svc, e.target.value);
                        e.target.value = "";
                      }} />
                    {!neverPulled && notPulled.length > 0 && (
                      <button type="button" className="text-[10px] text-sky-400 hover:text-sky-300"
                        onClick={() => onChange(attachTemplateMaterials(materials, [svc], templates))}
                        title={notPulled.join("\n")}>
                        + {notPulled.length} from Admin list
                      </button>
                    )}
                    <Link to="/settings/services?tab=services" className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-200"
                      title="The default list for this service lives in Admin → Services → edit → “Required materials from client”">
                      <Settings2 className="h-3 w-3" />Admin list
                    </Link>
                    {removed.length > 0 && (
                      <button type="button" className="text-[10px] text-zinc-500 hover:text-zinc-300"
                        onClick={() => setShowRemoved({ ...showRemoved, [svc]: !showRemoved[svc] })}>
                        Removed for this project: {removed.length} · {showRemoved[svc] ? "hide" : "show"}
                      </button>
                    )}
                  </div>
                  {showRemoved[svc] && removed.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 px-1 py-0.5 text-[11px] text-zinc-500">
                      <span className="line-through flex-1">{m.name}</span>
                      <button type="button" className="flex items-center gap-1 text-sky-400 hover:text-sky-300" onClick={() => setRow(m.id, { removed: false })}>
                        <RotateCcw className="h-3 w-3" />restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
